import {
  format,
  formatDistanceToNowStrict,
  intlFormat,
  differenceInCalendarDays,
  differenceInHours,
  differenceInMinutes,
  subWeeks,
  subMonths,
} from 'date-fns';
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';
import round from 'lodash/round';
import { ColDef } from 'ag-grid-community';
import { DateFormat } from '@/types/dateFormats';

export const formatTimestampWithFns = (
  timestamp?: string,
  dateSchema = DateFormat.DAY_MONTH_YEAR_WITH_HOURS_MINS_SECS,
  shouldSecureTimestamp = true,
  withUTCSuffix = false,
) => {
  if (!timestamp) return '';

  const securedTimestamp = shouldSecureTimestamp ? timestamp + 'Z' : timestamp; // add Z to treat timestamp as UTC

  const localDate = new Date(securedTimestamp); // local interpretation

  const formattedDate = formatInTimeZone(localDate, 'UTC', dateSchema);

  return withUTCSuffix ? `${formattedDate} UTC` : formattedDate;
};

export const formatTimestampWithFnsToLocalTime = (
  timestamp?: string,
  shouldSecureTimestamp = true,
  withTimeZoneSuffix = true,
) => {
  if (!timestamp) return '';

  const securedTimestamp = shouldSecureTimestamp ? timestamp + 'Z' : timestamp; // add Z to treat timestamp as UTC

  const localDate = new Date(securedTimestamp); // local interpretation
  if (isNaN(localDate.getTime())) return '';

  const { timeZone: localTimeZone, locale: localLocale } = Intl.DateTimeFormat().resolvedOptions();

  const formattedDate = intlFormat(
    localDate,
    { dateStyle: 'short', timeStyle: 'medium', timeZone: localTimeZone },
    { locale: localLocale },
  );

  return withTimeZoneSuffix ? `${formattedDate} ${localTimeZone}` : formattedDate;
};

export const formatDateWithFns = (
  date: Date,
  dateSchema = DateFormat.DAY_MONTH_YEAR_WITH_HOURS_MINS_SECS,
) => {
  return format(date, dateSchema);
};

/**
 * Tooltip text that keeps UTC as the primary reading and offers the viewer's
 * local time as a helper, e.g.
 * "17 Jun 2026, 12:35 UTC / 17/06/2026, 14:35:00 local time".
 */
export const getUtcLocalTimeTooltip = (timestamp?: string | null): string => {
  if (!timestamp) return '';

  const utc = formatTimestampWithFns(
    timestamp,
    DateFormat.DAY_MONTH_YEAR_WITH_HOURS_MINS,
    true,
    true,
  );
  const local = formatTimestampWithFnsToLocalTime(timestamp, true, false);

  return `${utc} / ${local} local time`;
};

export const getSubtractedDateByWeek = (timestamp: string, weekNumberToSubtract: number) => {
  const securedTimestamp = timestamp + 'Z'; // add Z to treat timestamp as UTC
  const localDate = new Date(securedTimestamp); // local interpretation

  const earlier = subWeeks(localDate, weekNumberToSubtract);

  earlier.setUTCHours(0, 0, 0, 0); // make sure that time will be reset in UTC
  const result = toZonedTime(earlier, 'UTC');

  return format(result, DateFormat.TIMESTAMP);
};

export const getSubtractedDateByMonth = (timestamp: string, monthNumberToSubtract: number) => {
  const securedTimestamp = timestamp + 'Z'; // add Z to treat timestamp as UTC
  const localDate = new Date(securedTimestamp); // local interpretation

  const earlier = subMonths(localDate, monthNumberToSubtract);

  earlier.setUTCHours(0, 0, 0, 0); // make sure that time will be reset in UTC
  const result = toZonedTime(earlier, 'UTC');

  return format(result, DateFormat.TIMESTAMP);
};

export const formatToUSD = (amount: number, noDecimals?: boolean) => {
  const noDecimalsOptions = noDecimals
    ? { minimumFractionDigits: 0, maximumFractionDigits: 0 }
    : {};
  return Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    ...noDecimalsOptions,
  }).format(amount);
};

export const formatRatio = (ratio: number, fractionDigits = 4): string =>
  `${ratio.toFixed(fractionDigits)}x`;

export const formatFloatToPercent = (value?: number | null, withPlus = false): string => {
  if (value === null || value === undefined) return '';

  if (withPlus) {
    return (value > 0 ? '+' : '') + value.toFixed(2) + '%';
  }

  return value.toFixed(2) + '%';
};

export const formatSignedUSD = (value: number, noDecimals?: boolean): string =>
  (value > 0 ? '+' : '') + formatToUSD(value, noDecimals);

export const formatFractionToBasisPoints = (
  fraction: number,
  withPlus = false,
  precision = 0,
): string => {
  const bps = fraction * 10_000;
  const prefix = withPlus && bps > 0 ? '+' : '';
  return `${prefix}${bps.toFixed(precision)} bps`;
};

export const getListOfNumbers = (length: number, startFromZero = true): number[] =>
  Array.from({ length }, (_, index) => (startFromZero ? index : index + 1));

export const sortAlphabetically = (items: string[]) => {
  return items.sort((a, b) => {
    const nameA = a.toLowerCase();
    const nameB = b.toLowerCase();

    if (nameA < nameB) return -1;
    if (nameA > nameB) return 1;
    return 0;
  });
};

const getMinutesDifference = (targetDate: Date, currentDate: Date) => {
  const minutesDifferenceNumber = differenceInMinutes(currentDate, targetDate);

  if (minutesDifferenceNumber === 1) {
    return `${minutesDifferenceNumber}min ago`;
  }
  return `${minutesDifferenceNumber}min ago`;
};

const getHoursDifference = (targetDate: Date, currentDate: Date, minutesVariant = true) => {
  const hoursDifferenceNumber = differenceInHours(currentDate, targetDate);

  if (minutesVariant && hoursDifferenceNumber === 0) {
    return getMinutesDifference(targetDate, currentDate);
  }

  if (hoursDifferenceNumber === 1) {
    return `${hoursDifferenceNumber}hr ago`;
  }

  return `${hoursDifferenceNumber}hr ago`;
};

const getCalendarDaysDifference = (
  targetDate: Date,
  currentDate: Date,
  hoursVariant = true,
  minutesVariant = true,
) => {
  const daysDifferenceNumber = differenceInCalendarDays(currentDate, targetDate);

  if (hoursVariant && daysDifferenceNumber === 0) {
    return getHoursDifference(targetDate, currentDate, minutesVariant);
  }

  if (daysDifferenceNumber === 1) {
    return `${daysDifferenceNumber}d ago`;
  }

  return `${daysDifferenceNumber}d ago`;
};

export const getTimestampDifferenceFromNow = (
  target?: string | null,
  hoursVariant = true,
  minutesVariant = true,
) => {
  if (!target) return null;

  //format timestamp to type Date with no changed value (timestamp from BE is already in UTC)
  const targetDate = toZonedTime(`${target}Z`, 'UTC');

  //get the currentDate of UTC time
  const currentDate = toZonedTime(new Date(), 'UTC');

  //compare both dates in UTC
  return getCalendarDaysDifference(targetDate, currentDate, hoursVariant, minutesVariant);
};

const TIME_ZONE_SUFFIX = /(Z|[+-]\d{2}:?\d{2})$/;

export const getRelativeTimeFromNow = (timestamp?: string | null) => {
  if (!timestamp) return null;

  // timestamps without a zone come from BE already in UTC, so the marker is added rather than read
  const targetDate = new Date(TIME_ZONE_SUFFIX.test(timestamp) ? timestamp : `${timestamp}Z`);

  if (isNaN(targetDate.getTime())) return null;

  return formatDistanceToNowStrict(targetDate, { addSuffix: true });
};

export const capitalizeFirstLetters = (text: string | null) => {
  if (text === null) return '';

  return text
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const DOLLAR_UNITS = [
  { divisor: 1, suffix: '' },
  { divisor: 1_000, suffix: 'K' },
  { divisor: 1_000_000, suffix: 'M' },
  { divisor: 1_000_000_000, suffix: 'B' },
];

export const formatLargeDollarValueWithSuffix = (
  value: number,
  precision = 2,
  skipDollar = false,
) => {
  const dollar = skipDollar ? '' : '$';
  const sign = value < 0 ? '-' : '';
  const absValue = Math.abs(value);
  const scaleTo = (index: number) => round(absValue / DOLLAR_UNITS[index].divisor, precision);

  let unitIndex = Math.max(
    DOLLAR_UNITS.findLastIndex(({ divisor }) => absValue >= divisor),
    0,
  );
  let scaled = scaleTo(unitIndex);

  if (scaled >= 1_000 && unitIndex < DOLLAR_UNITS.length - 1) {
    unitIndex += 1;
    scaled = scaleTo(unitIndex);
  }

  const { suffix } = DOLLAR_UNITS[unitIndex];
  const formatted = suffix === 'B' ? scaled.toLocaleString('en-US') : scaled.toString();

  return `${sign}${dollar}${formatted}${suffix}`;
};

export const formatSignedLargeDollarValueWithSuffix = (value: number, precision = 2): string =>
  (value > 0 ? '+' : '') + formatLargeDollarValueWithSuffix(value, precision);

export const MUSD_TO_USD = 1_000_000;

/** Formats a value expressed in millions of USD (MUSD) as an adaptive $K/$M/$B/$T string. */
export const formatMusdAsDollars = (musd: number, precision = 2): string =>
  formatLargeDollarValueWithSuffix(musd * MUSD_TO_USD, precision);

export const formatSignedMusdAsDollars = (musd: number, precision = 2): string =>
  formatSignedLargeDollarValueWithSuffix(musd * MUSD_TO_USD, precision);

export const returnWrappedString = (text: string | null, length: number) => {
  if (text === null) return '';
  return text.slice(0, length) + '...';
};

export const w = <T>(n: number): Partial<ColDef<T>> => ({ width: n });

export const minW = <T>(n: number): Partial<ColDef<T>> => ({ minWidth: n });
